import React, { useState, useEffect, useRef } from 'react';
import Drawer from 'react-modern-drawer';
import 'react-modern-drawer/dist/index.css';
import { useForm } from 'react-hook-form';
import { Icon } from '@iconify/react/dist/iconify.js';
import { addCountry, updateCountry, uploadCountryGeography } from '../lib/api';
import * as XLSX from 'xlsx-js-style';

// Import country data from npm package
const countryData = require('country-code-flag-phone-extension-json');
const API_BASE = process.env.REACT_APP_API_BASE || "https://api.soksan-dss.com";


const CountryDrawer = ({ isOpen, onClose, countryToEdit, onSuccess }) => {
    const [message, setMessage] = useState(null);
    const [currentFlagPreview, setCurrentFlagPreview] = useState(null);
    const [newFlagPreview, setNewFlagPreview] = useState(null);
    const [saveLoading, setSaveLoading] = useState(false);
    const [authError, setAuthError] = useState(false);
    const [countriesList, setCountriesList] = useState([]);
    const [excelData, setExcelData] = useState(null);
    const [excelFileName, setExcelFileName] = useState('');
    const [excelUploadLoading, setExcelUploadLoading] = useState(false);
    const fileInputRef = useRef(null);
    const excelFileInputRef = useRef(null);

    const { register, handleSubmit, formState: { errors }, reset, setValue, watch, trigger } = useForm({
        defaultValues: {
            CountryName: countryToEdit?.CountryName || '',
            CountryCode: countryToEdit?.CountryCode || '',
            PhoneMask: countryToEdit?.PhoneMask || '',
            TimeZone: countryToEdit?.TimeZone || '',
            level1: '',
            level2: '',
            level3: '',
            level4: '',
            level5: '',
            ImageOfFlag: null,
        },
    });

    const watchCountryName = watch('CountryName');
    const watchLevel1 = watch('level1');
    const watchLevel2 = watch('level2');
    const watchLevel3 = watch('level3');
    const watchLevel4 = watch('level4');
    const watchLevel5 = watch('level5');

    // Helper to get active configured level names as array
    const getActiveLevels = () => {
        const levels = [watchLevel1, watchLevel2, watchLevel3, watchLevel4, watchLevel5];
        const trimmed = levels.map(l => (l || '').trim()).filter(Boolean);
        return trimmed.length > 0 ? trimmed : ['Province', 'District', 'Commune', 'Village'];
    };

    const getAuthToken = () => {
        const token = localStorage.getItem("token") ||
            localStorage.getItem("authToken") ||
            localStorage.getItem("accessToken");

        if (!token) {
            console.error("No authentication token found in localStorage");
            setAuthError(true);
            setMessage({
                type: 'error',
                text: 'Authentication token not found. Please login again.'
            });
            return null;
        }

        return token;
    };

    useEffect(() => {
        if (!countryToEdit) {
            try {
                const countries = countryData.all({
                    keys: ["id", "name", "dail_code", "phone_mask", "emoji", "timezones"],
                    sortBy: ["US", "IN", "GB", "CA", "AU"]
                });

                if (countries && countries.rows) {
                    setCountriesList(countries.rows);
                } else if (Array.isArray(countries)) {
                    setCountriesList(countries);
                }
            } catch (error) {
                console.error("Error loading countries:", error);
                setCountriesList([]);
            }
        }
    }, [countryToEdit]);

    useEffect(() => {
        if (!countryToEdit && watchCountryName && countriesList.length > 0) {
            const country = countriesList.find(c => c.name === watchCountryName);
            if (country) {
                setValue('CountryCode', country.dial_code || '');
                const mask = country.phone_mask || '';
                const maskLength = mask.replace(/[-()]/g, '').length;
                setValue('PhoneMask', maskLength || '');
                setValue('CountryCode', country.dail_code || '');
                if (country.timezones && country.timezones.length > 0) {
                    setValue('TimeZone', country.timezones[0].gmtOffsetName || '');
                }
            }
        }
    }, [watchCountryName, countryToEdit, countriesList, setValue, trigger]);

    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
            setAuthError(false);
            setMessage(null);
            setExcelData(null);
            setExcelFileName('');

            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
            if (excelFileInputRef.current) {
                excelFileInputRef.current.value = '';
            }

            if (countryToEdit) {
                let timeZoneValue = countryToEdit.TimeZone || '';

                if (!timeZoneValue && countryToEdit.CountryName) {
                    try {
                        const allData = countryData.all();
                        const countries = Array.isArray(allData) ? allData : (allData.rows || []);
                        const matchedCountry = countries.find(c => c.name === countryToEdit.CountryName);
                        if (matchedCountry && matchedCountry.timezones && matchedCountry.timezones.length > 0) {
                            timeZoneValue = matchedCountry.timezones[0].gmtOffsetName || '';
                        }
                    } catch (e) {
                        console.error("Error finding fallback timezone", e);
                    }
                }

                // Parse CountryLevels if existing
                let levelsObj = {
                    level1: '',
                    level2: '',
                    level3: '',
                    level4: '',
                    level5: '',
                };

                if (countryToEdit.CountryLevels) {
                    let parsedLevels = countryToEdit.CountryLevels;
                    if (typeof parsedLevels === 'string') {
                        try {
                            parsedLevels = JSON.parse(parsedLevels);
                        } catch (e) {
                            console.error("Error parsing CountryLevels", e);
                        }
                    }

                    if (Array.isArray(parsedLevels)) {
                        levelsObj = {
                            level1: parsedLevels[0] || '',
                            level2: parsedLevels[1] || '',
                            level3: parsedLevels[2] || '',
                            level4: parsedLevels[3] || '',
                            level5: parsedLevels[4] || '',
                        };
                    } else if (typeof parsedLevels === 'object' && parsedLevels !== null) {
                        levelsObj = {
                            level1: parsedLevels.level1 || parsedLevels.level_1 || parsedLevels[0] || '',
                            level2: parsedLevels.level2 || parsedLevels.level_2 || parsedLevels[1] || '',
                            level3: parsedLevels.level3 || parsedLevels.level_3 || parsedLevels[2] || '',
                            level4: parsedLevels.level4 || parsedLevels.level_4 || parsedLevels[3] || '',
                            level5: parsedLevels.level5 || parsedLevels.level_5 || parsedLevels[4] || '',
                        };
                    }
                }

                reset({
                    CountryName: countryToEdit.CountryName || '',
                    CountryCode: countryToEdit.CountryCode || '',
                    PhoneMask: countryToEdit.PhoneMask || '',
                    TimeZone: timeZoneValue,
                    level1: levelsObj.level1,
                    level2: levelsObj.level2,
                    level3: levelsObj.level3,
                    level4: levelsObj.level4,
                    level5: levelsObj.level5,
                    ImageOfFlag: null,
                });

                // Set current flag preview
                if (countryToEdit.ImageOfFlag) {
                    const imageUrl = countryToEdit.ImageOfFlag.startsWith('http')
                        ? countryToEdit.ImageOfFlag
                        : `${API_BASE}/uploads/flag/${countryToEdit.ImageOfFlag}`;
                    setCurrentFlagPreview(imageUrl);
                } else {
                    setCurrentFlagPreview(null);
                }
                setNewFlagPreview(null);
            } else {
                reset({
                    CountryName: '',
                    CountryCode: '',
                    PhoneMask: '',
                    TimeZone: '',
                    level1: '',
                    level2: '',
                    level3: '',
                    level4: '',
                    level5: '',
                    ImageOfFlag: null,
                });
                setCurrentFlagPreview(null);
                setNewFlagPreview(null);
            }
        }

        return () => {
            document.body.style.overflow = 'auto';
        };
    }, [isOpen, countryToEdit, reset]);

    // Handle file selection for flag
    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (!file.type.startsWith('image/')) {
                setMessage({ type: 'error', text: 'Please select an image file' });
                if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                }
                return;
            }

            if (file.size > 5 * 1024 * 1024) {
                setMessage({ type: 'error', text: 'Image size should be less than 5MB' });
                if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                }
                return;
            }

            setValue('ImageOfFlag', file, { shouldValidate: true });

            const reader = new FileReader();
            reader.onloadend = () => {
                setNewFlagPreview(reader.result);
            };
            reader.readAsDataURL(file);

            setMessage(null);
        } else {
            setValue('ImageOfFlag', null);
            setNewFlagPreview(null);
        }
    };

    // Handle Excel file upload
    const handleExcelFileChange = (e) => {
        const file = e.target.files[0];
        if (!file) {
            setExcelData(null);
            setExcelFileName('');
            return;
        }

        setExcelUploadLoading(true);
        setMessage(null);

        // Check file extension
        const fileExtension = file.name.split('.').pop().toLowerCase();
        if (!['xlsx', 'xls', 'csv'].includes(fileExtension)) {
            setMessage({
                type: 'error',
                text: 'Please select an Excel file (.xlsx, .xls, or .csv)'
            });
            if (excelFileInputRef.current) {
                excelFileInputRef.current.value = '';
            }
            setExcelUploadLoading(false);
            return;
        }

        const reader = new FileReader();
        reader.onload = (event) => {
            try {
                const data = new Uint8Array(event.target.result);
                const workbook = XLSX.read(data, { type: 'array' });
                const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
                const jsonData = XLSX.utils.sheet_to_json(firstSheet);

                const activeLevels = getActiveLevels();

                // Validate required columns based on defined active levels
                if (jsonData.length > 0) {
                    const firstRow = jsonData[0];
                    const missingColumns = activeLevels.filter(col => !(col in firstRow));

                    if (missingColumns.length > 0) {
                        setMessage({
                            type: 'error',
                            text: `Missing required columns: ${missingColumns.join(', ')}. Please use the sample template.`
                        });
                        setExcelData(null);
                        setExcelFileName('');
                        if (excelFileInputRef.current) {
                            excelFileInputRef.current.value = '';
                        }
                    } else {
                        // Store both the file and the parsed data
                        setExcelData({
                            file: file, // Store the actual file for upload
                            fileName: file.name,
                            data: jsonData, // Store parsed data for preview
                            rowCount: jsonData.length,
                            columns: activeLevels
                        });
                        setExcelFileName(file.name);
                        setMessage({
                            type: 'success',
                            text: `Excel file loaded successfully! Found ${jsonData.length} rows.`
                        });
                    }
                } else {
                    setMessage({
                        type: 'error',
                        text: 'The Excel file is empty'
                    });
                    setExcelData(null);
                    setExcelFileName('');
                }
            } catch (error) {
                console.error('Error parsing Excel:', error);
                setMessage({
                    type: 'error',
                    text: 'Error parsing Excel file. Please check the file format.'
                });
                setExcelData(null);
                setExcelFileName('');
            } finally {
                setExcelUploadLoading(false);
            }
        };

        reader.onerror = () => {
            setMessage({
                type: 'error',
                text: 'Error reading file'
            });
            setExcelUploadLoading(false);
        };

        reader.readAsArrayBuffer(file);
    };

    // Download sample Excel template based on defined levels
    const downloadSampleExcel = () => {
        try {
            const wb = XLSX.utils.book_new();

            // Dynamic headers from configured levels
            const headers = getActiveLevels();

            // Create sheet from an array containing only the header row
            const ws = XLSX.utils.aoa_to_sheet([headers]);

            /* ---------------- HEADER STYLE ---------------- */
            const headerStyle = {
                font: { bold: true, color: { rgb: "FFFFFF" }, sz: 12 },
                fill: { fgColor: { rgb: "1A73E8" } },
                alignment: { horizontal: "center", vertical: "center" },
                border: {
                    top: { style: "thin" }, bottom: { style: "thin" },
                    left: { style: "thin" }, right: { style: "thin" }
                }
            };

            // Apply style to each header cell
            headers.forEach((_, index) => {
                const cellAddress = XLSX.utils.encode_cell({ r: 0, c: index });
                if (ws[cellAddress]) {
                    ws[cellAddress].s = headerStyle;
                }
            });

            /* ---------------- COLUMN WIDTH ---------------- */
            ws["!cols"] = headers.map(() => ({ wch: 20 }));

            /* ---------------- ADD SHEET ---------------- */
            XLSX.utils.book_append_sheet(wb, ws, "Locations");

            const countrySlug = (watchCountryName || countryToEdit?.CountryName || 'location').toLowerCase().replace(/\s+/g, '_');
            XLSX.writeFile(
                wb,
                `${countrySlug}_template_${new Date().toISOString().split("T")[0]}.xlsx`
            );

            setMessage({
                type: "success",
                text: `Template file with columns (${headers.join(' > ')}) downloaded successfully!`
            });

        } catch (error) {
            console.error(error);
            setMessage({
                type: "error",
                text: "Error downloading sample file"
            });
        }
    };

    const handleImageLabelClick = (e) => {
        e.preventDefault();
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
            fileInputRef.current.click();
        }
    };

    const handleExcelLabelClick = (e) => {
        e.preventDefault();
        if (excelFileInputRef.current) {
            excelFileInputRef.current.value = '';
            excelFileInputRef.current.click();
        }
    };

    const removeExcelFile = () => {
        setExcelData(null);
        setExcelFileName('');
        if (excelFileInputRef.current) {
            excelFileInputRef.current.value = '';
        }
    };

    const onFormSubmit = async (data) => {
        try {
            setSaveLoading(true);
            setMessage(null);
            setAuthError(false);

            const token = getAuthToken();
            if (!token) {
                setSaveLoading(false);
                return;
            }

            if (countryToEdit) {
                if (!data.CountryName || !data.CountryCode) {
                    setMessage({ type: 'error', text: 'Please fill all required fields' });
                    setSaveLoading(false);
                    return;
                }
            }

            const countryLevels = {
                level1: data.level1?.trim() || '',
                level2: data.level2?.trim() || '',
                level3: data.level3?.trim() || '',
                level4: data.level4?.trim() || '',
                level5: data.level5?.trim() || '',
            };
            const countryLevelsJson = JSON.stringify(countryLevels);

            let flagUpdateSuccess = true;
            let geographyResponse = null;

            if (countryToEdit) {
                // EDIT MODE - Handle flag update, timezone, CountryLevels, and geography upload
                const flagFormData = new FormData();
                let hasChanges = false;

                if (data.TimeZone !== undefined) {
                    flagFormData.append('TimeZone', data.TimeZone || '');
                    hasChanges = true;
                }
                if (data.ImageOfFlag) {
                    flagFormData.append('ImageOfFlag', data.ImageOfFlag);
                    hasChanges = true;
                }
                flagFormData.append('CountryLevels', countryLevelsJson);
                hasChanges = true;

                if (hasChanges) {
                    try {
                        const flagResponse = await updateCountry(countryToEdit.ID, flagFormData, token);
                        if (!flagResponse.success) {
                            flagUpdateSuccess = false;
                            throw new Error(flagResponse.message || 'Failed to update country');
                        }
                    } catch (flagError) {
                        flagUpdateSuccess = false;
                        throw flagError;
                    }
                }

                // 2. If Excel file is uploaded, send it to the geography endpoint
                if (excelData && excelData.file) {
                    const excelFormData = new FormData();
                    excelFormData.append('excelFile', excelData.file);

                    try {
                        geographyResponse = await uploadCountryGeography(countryToEdit.ID, excelFormData, token);

                        if (!geographyResponse.success) {
                            throw new Error(geographyResponse.message || 'Failed to upload geography data');
                        }
                    } catch (geoError) {
                        throw geoError;
                    }
                }

                // Determine success message based on what was updated
                if (geographyResponse) {
                    const stats = geographyResponse.data;
                    if (stats.hierarchy) {
                        setMessage({
                            type: 'success',
                            text: `✓ Country updated successfully!\n` +
                                `Processed ${stats.recordsProcessed} locations\n` +
                                `${stats.hierarchy.provinces?.count || 0} ${countryLevels.level1 || 'provinces'}\n` +
                                `${stats.hierarchy.districts?.count || 0} ${countryLevels.level2 || 'districts'}\n` +
                                `${stats.hierarchy.communes?.count || 0} ${countryLevels.level3 || 'communes'}\n` +
                                `${stats.hierarchy.villages?.count || 0} ${countryLevels.level4 || 'villages'}`
                        });
                    } else {
                        setMessage({
                            type: 'success',
                            text: `✓ Country updated successfully!\n` +
                                `Total Rows: ${stats.totalRows}\n` +
                                `Processed: ${stats.recordsProcessed}\n` +
                                `Skipped: ${stats.skippedRows}\n` +
                                `Database Verified: ${stats.databaseVerification?.totalVillagesInDb} records`
                        });
                    }
                } else if (flagUpdateSuccess) {
                    setMessage({
                        type: 'success',
                        text: 'Country updated successfully!'
                    });
                }

            } else {
                // ADD MODE - Add new country
                const formData = new FormData();
                formData.append('CountryName', data.CountryName);
                formData.append('CountryCode', data.CountryCode);
                formData.append('PhoneMask', data.PhoneMask || '');
                formData.append('TimeZone', data.TimeZone || '');
                formData.append('CountryLevels', countryLevelsJson);

                if (data.ImageOfFlag) {
                    formData.append('ImageOfFlag', data.ImageOfFlag);
                }

                const response = await addCountry(formData, token);

                if (response.success) {
                    setMessage({
                        type: 'success',
                        text: 'Country added successfully!'
                    });
                } else {
                    throw new Error(response.message || 'Failed to add country');
                }
            }

            // Success - close drawer after delay
            if (onSuccess) {
                onSuccess();
            }

            setTimeout(() => {
                onClose();
                reset();
                setExcelData(null);
                setExcelFileName('');
                setSaveLoading(false);
            }, 3000);

        } catch (error) {
            console.error('Save error:', error);
            setMessage({
                type: 'error',
                text: error.response?.data?.message || error.message || 'An error occurred while saving. Please try again.'
            });
            setSaveLoading(false);
        }
    };

    return (
        <>
            <Drawer
                open={isOpen}
                onClose={onClose}
                direction="right"
                size={700}
                style={{ zIndex: 1050 }}
                className="p-0"
            >
                <div className="drawer-header">
                    <h6 className="drawer-title">
                        {countryToEdit ? 'Edit Country' : 'Add New Country'}
                    </h6>
                    <button
                        onClick={onClose}
                        className='drawer-close'
                    >
                        <Icon icon="lucide:x" />
                    </button>
                </div>

                <div className="card-body" style={{ maxHeight: 'calc(100vh - 80px)', overflowY: 'auto' }}>
                    {message && (
                        <div className={`alert alert-${message.type === 'success' ? 'success' : 'danger'} alert-dismissible fade show`} role="alert">
                            {message.text}
                            <button type="button" className="btn-close" onClick={() => setMessage(null)}></button>
                        </div>
                    )}

                    {saveLoading && (
                        <div className="loading-overlay">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        </div>
                    )}

                    <form onSubmit={handleSubmit(onFormSubmit)}>
                        {/* Country Name */}
                        <div className="mb-20">
                            <label htmlFor="CountryName" className="form-label">
                                Country Name <span className="required-star">*</span>
                            </label>
                            {countryToEdit ? (
                                <input
                                    type="text"
                                    id="CountryName"
                                    className="form-control"
                                    value={countryToEdit.CountryName}
                                    disabled
                                    readOnly
                                />
                            ) : (
                                <>
                                    <select
                                        id="CountryName"
                                        className={`form-select ${errors.CountryName ? 'border-danger-600' : ''}`}
                                        {...register('CountryName', {
                                            required: 'Country Name is required',
                                        })}
                                        disabled={countriesList.length === 0}
                                    >
                                        <option value="">Select Country</option>
                                        {countriesList.map((country) => (
                                            <option key={country.id} value={country.name}>
                                                {country.emoji} {country.name}
                                            </option>
                                        ))}
                                    </select>
                                    {errors.CountryName && (
                                        <span className="error-message">{errors.CountryName.message}</span>
                                    )}
                                </>
                            )}
                        </div>

                        {/* Country Code */}
                        <div className="mb-20">
                            <label htmlFor="CountryCode" className="form-label">
                                Country Code <span className="required-star">*</span>
                            </label>
                            <input
                                type="text"
                                id="CountryCode"
                                className={`form-control ${errors.CountryCode ? 'border-danger-600' : ''}`}
                                {...register('CountryCode', {
                                    required: 'Country Code is required',
                                })}
                                disabled={!!countryToEdit}
                                readOnly
                            />
                            {errors.CountryCode && (
                                <span className="error-message">{errors.CountryCode.message}</span>
                            )}
                        </div>

                        {/* Phone Mask */}
                        <div className="mb-20">
                            <label htmlFor="PhoneMask" className="form-label">
                                Phone Mask
                            </label>
                            <input
                                type="text"
                                id="PhoneMask"
                                className={`form-control ${errors.PhoneMask ? 'border-danger-600' : ''}`}
                                {...register('PhoneMask')}
                                disabled={!!countryToEdit}
                                placeholder="e.g., +93-##-###-####"
                            />
                            {errors.PhoneMask && (
                                <span className="error-message">{errors.PhoneMask.message}</span>
                            )}
                        </div>

                        {/* Time Zone */}
                        <div className="mb-20">
                            <label htmlFor="TimeZone" className="form-label">
                                Time Zone
                            </label>
                            <input
                                type="text"
                                id="TimeZone"
                                className={`form-control ${errors.TimeZone ? 'border-danger-600' : ''}`}
                                {...register('TimeZone')}
                            />
                        </div>

                        {/* Country Levels (Hierarchy Setup) */}
                        <div className="mb-20">
                            <div className="d-flex align-items-center justify-content-between mb-8">
                                <label className="form-label mb-0">
                                    Administrative / Geography Levels (5 Levels)
                                </label>
                            </div>
                            <div className="p-16 border radius-8 bg-neutral-50 mb-12">
                                <div className="row g-2">
                                    <div className="col-12 col-md-4 mb-2">
                                        <label className="form-label text-xs fw-semibold mb-4">Level 1</label>
                                        <input
                                            type="text"
                                            className="form-control form-control-sm"
                                            placeholder="Country"
                                            {...register('level1')}
                                        />
                                    </div>
                                    <div className="col-12 col-md-4 mb-2">
                                        <label className="form-label text-xs fw-semibold mb-4">Level 2</label>
                                        <input
                                            type="text"
                                            className="form-control form-control-sm"
                                            placeholder="Provience"
                                            {...register('level2')}
                                        />
                                    </div>
                                    <div className="col-12 col-md-4 mb-2">
                                        <label className="form-label text-xs fw-semibold mb-4">Level 3</label>
                                        <input
                                            type="text"
                                            className="form-control form-control-sm"
                                            placeholder="Distric"
                                            {...register('level3')}
                                        />
                                    </div>
                                    <div className="col-12 col-md-6 mb-2">
                                        <label className="form-label text-xs fw-semibold mb-4">Level 4</label>
                                        <input
                                            type="text"
                                            className="form-control form-control-sm"
                                            placeholder="Commune"
                                            {...register('level4')}
                                        />
                                    </div>
                                    <div className="col-12 col-md-6 mb-2">
                                        <label className="form-label text-xs fw-semibold mb-4">Level 5</label>
                                        <input
                                            type="text"
                                            className="form-control form-control-sm"
                                            placeholder="Village"
                                            {...register('level5')}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Flag Image Upload */}
                        <div className="mb-20">
                            <label className="form-label d-flex align-items-center">
                                Flag Image {!countryToEdit && <span className="required-star"> *</span>}
                            </label>

                            <div className="file-input-wrapper">
                                <label
                                    className={`file-input-label ${saveLoading || authError ? 'disabled' : ''}`}
                                    onClick={handleImageLabelClick}
                                >
                                    <div className="file-input-text">
                                        <Icon icon="bi:image" />
                                        <span>
                                            {countryToEdit
                                                ? newFlagPreview ? 'Change selected flag' : 'Click to select new flag image'
                                                : newFlagPreview ? 'Change selected flag' : 'Click to upload flag image'}
                                        </span>
                                    </div>
                                </label>
                                <input
                                    ref={fileInputRef}
                                    id="flagUpload"
                                    type="file"
                                    accept="image/*"
                                    className="file-input"
                                    onChange={handleFileChange}
                                    disabled={saveLoading || authError}
                                    required={!countryToEdit}
                                />
                            </div>

                            {newFlagPreview && (
                                <div className="image-preview-container">
                                    <img
                                        src={newFlagPreview}
                                        alt="New flag preview"
                                        className="image-preview"
                                    />
                                    <span className="preview-label">New Flag Preview</span>
                                </div>
                            )}

                            {countryToEdit && currentFlagPreview && !newFlagPreview && (
                                <div className="current-preview-section">
                                    <div className="image-preview-container">
                                        <img
                                            src={currentFlagPreview}
                                            alt="Current flag"
                                            className="image-preview"
                                        />
                                        <span className="preview-label">Current Flag</span>
                                    </div>
                                </div>
                            )}

                            {errors.ImageOfFlag && (
                                <span className="error-message">{errors.ImageOfFlag.message}</span>
                            )}
                        </div>

                        {/* Excel Upload Section - Only show for edit mode */}
                        {countryToEdit && (
                            <div className="mb-20">
                                <label className="form-label d-flex align-items-center">
                                    Upload Locations (Excel)
                                </label>

                                <div className="excel-upload-container">
                                    <div className="excel-header">
                                        <div className="excel-title">
                                            <Icon icon="bi:file-earmark-spreadsheet" className="text-success" />
                                            <span>Import Geography Data</span>
                                        </div>
                                        <button
                                            type="button"
                                            className="download-template-btn"
                                            onClick={downloadSampleExcel}
                                            disabled={saveLoading || excelUploadLoading}
                                        >
                                            <Icon icon="bi:download" />
                                            Sample Template
                                        </button>
                                    </div>

                                    {!excelData && !excelUploadLoading && (
                                        <>
                                            <input
                                                ref={excelFileInputRef}
                                                id="excelUpload"
                                                type="file"
                                                accept=".xlsx,.xls,.csv"
                                                style={{ display: 'none' }}
                                                onChange={handleExcelFileChange}
                                                disabled={saveLoading || excelUploadLoading}
                                            />
                                            <div 
                                                className={`upload-area ${saveLoading ? 'disabled' : ''}`}
                                                onClick={handleExcelLabelClick}
                                            >
                                            <Icon icon="bi:cloud-arrow-up" className="upload-icon" />
                                            <div className="upload-text">Click to upload Excel file</div>
                                            <div className="upload-hint">Headers should match: {getActiveLevels().join(', ')}</div>
                                            </div>
                                        </>
                                    )}

                                    {excelUploadLoading && (
                                        <div className="loading-spinner-container">
                                            <div className="spinner-border spinner-border-sm text-primary" role="status">
                                                <span className="visually-hidden">Loading...</span>
                                            </div>
                                            <span className="text-sm">Processing file...</span>
                                        </div>
                                    )}

                                    {excelData && (
                                        <div className="preview-card">
                                            <div className="preview-header">
                                                <div className="file-details">
                                                    <Icon icon="bi:file-earmark-excel-fill" className="file-icon" />
                                                    <div>
                                                        <span className="file-name">{excelFileName}</span>
                                                        <span className="file-meta">{excelData.rowCount} rows found</span>
                                                    </div>
                                                </div>
                                                <button
                                                    type="button"
                                                    className="remove-btn"
                                                    onClick={removeExcelFile}
                                                    disabled={saveLoading}
                                                    title="Remove file"
                                                >
                                                    <Icon icon="bi:trash" />
                                                </button>
                                            </div>

                                            <div className="preview-table-container">
                                                <table className="preview-table">
                                                    <thead>
                                                        <tr>
                                                            {(excelData.columns || getActiveLevels()).map((col, idx) => (
                                                                <th key={idx}>{col}</th>
                                                            ))}
                                                        </tr>
                                                        </thead>
                                                        <tbody>
                                                            {excelData.data.slice(0, 5).map((row, idx) => (
                                                                <tr key={idx}>
                                                                    {(excelData.columns || getActiveLevels()).map((col, cIdx) => (
                                                                        <td key={cIdx}>{row[col] || '-'}</td>
                                                                    ))}
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                {excelData.data.length > 5 && (
                                                    <div className="text-center p-2 bg-light text-muted text-xs border-top">
                                                        ... and {excelData.data.length - 5} more rows
                                                    </div>
                                                )}
                                                </div>
                                            </div>
                                    )}
                                </div>
                            </div>
                        )}

                        <div className="d-flex align-items-center justify-content-center gap-3 mt-24">
                            <button
                                type="button"
                                className="btn btn-secondary px-48 py-10 radius-8"
                                onClick={onClose}
                                disabled={saveLoading}
                            >
                                Cancel
                            </button>

                            <button
                                type="submit"
                                className="btn btn-primary px-48 py-10 radius-8 d-flex align-items-center gap-2"
                                disabled={saveLoading || authError}
                            >
                                {saveLoading ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm"></span>
                                        {countryToEdit ? 'Updating...' : 'Saving...'}
                                    </>
                                ) : (
                                    countryToEdit ? 'Update Country' : 'Save Country'
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </Drawer>
        </>
    );
};

export default CountryDrawer;